import { cep } from '@polvo-labs/form-utils'
import Axios from 'axios'
// @ts-ignore
import brazilStates from 'estados-br-json'
import React from 'react'
import Autocomplete from '../../components/Form/Fields/Autocomplete'
import Hidden from '../../components/Form/Fields/Hidden'
import CheckBox from '../../components/Form/Fields/CheckBox'
import Select from '../../components/Form/Fields/Select'
import FieldType from '../../components/Form/Fields/type'
import Upload from '../../components/Form/Fields/Upload'
import Text from '../../components/Text'
import { currentYearValidation, termsRequiredValidation } from '../../utils/form-validations'
import { useAuth } from '@polvo-labs/react-auth'


export interface AddressFormData {
  'address.zip_code': string
  'address.street': string
  'address.number': string
  'address.complement': string
  'address.neighborhood': string
  'address.city': string
  'address.state': string
}

const ADDRESS: FieldType<keyof AddressFormData>[] = [
  {
    name: 'address.zip_code',
    size: 1 / 4,
    data: {
      label: 'CEP',
    },
    html: {
      placeholder: 'Digite o CEP do imóvel',
      maxLength: 9,
    },
    validation: {
      format: (value: any) => {
        return cep.format(value)
      },
      parse: (value: any) => {
        return cep.parse(value)
      },
      validate: async (value: string, values: any, meta: any) => {
        if (value) {
          if (value.length === 8) {
            try {
              const response = await Axios({
                method: 'GET',
                url: `${process.env.REACT_APP_API_URL}/viacep/${cep.format(value)}`,
              })
              const data = await response.data.data
              Object.assign(values, {
                address: { ...values.address, city: data.city, state: data.state, neighborhood: data.neighborhood },
              })
              if (data.street) {
                Object.assign(values, {
                  address: { ...values.address, street: data.street },
                })
              }
            }
            catch (error) {
              return 'CEP inválido'
            }
          } else {
            return 'CEP inválido'
          }
        }
      },
    },
  },
  {
    name: 'address.street',
    size: 1 / 2,
    data: {
      label: 'Endereço',
    },
    html: {
      placeholder: 'Informe o endereço do imóvel',
      maxLength: 255,
    },
  },
  {
    name: 'address.number',
    size: 1 / 4,
    data: {
      label: 'Número',
    },
    html: {
      placeholder: 'Informe o número do imóvel',
      type: 'number',
    },
  },
  {
    name: 'address.complement',
    data: {
      label: 'Complemento',
    },
    html: {
      placeholder: 'Informe o complemento do imóvel',
      maxLength: 255,
    },
  },
  {
    name: 'address.neighborhood',
    size: 1 / 3,
    data: {
      label: 'Bairro',
    },
    html: {
      placeholder: 'Informe o bairro do imóvel',
      maxLength: 255,
    },
    // validation: required
  },
  {
    name: 'address.city',
    size: 1 / 3,
    component: Autocomplete,
    data: {
      label: 'Cidade',
      options: [],
    },
    html: {
      placeholder: 'Informe a cidade do imóvel',
      maxLength: 255,
    },
    validation: {
      validate: (value: string, values: any) => {
        if (value && value.length >= 3) {
          Axios({
            method: 'GET',
            url: `${process.env.REACT_APP_API_URL}/cities/${value}/search`,
          }).then(({ data }) => {
            data.data.forEach((element: any) => {
              if (element.city === value) {
                const state = element.state.uf
                Object.assign(values, {
                  address: { ...values.address, state: state },
                })
              }
            })
          })
        }
      },
    },
  },
  {
    name: 'address.state',
    component: Select,
    size: 1 / 3,
    data: {
      label: 'Estado',
      options: [{
        label: 'Selecione',
        value: '',
      }, ...brazilStates],
    },
    html: {
      placeholder: 'Informe o estado do imóvel',
    },
    // validation: required
  },
]

export interface CommonFormData extends AddressFormData {
  fiscal_information: string
  registration_number: string
  registry_office: string
  owes_since: string
  uploads: { id: number }[]
  accept_terms_conditions: boolean
  accept_privacy_policy: boolean
  adult: boolean
}
const COMMON: FieldType<keyof CommonFormData>[] = [
  ...ADDRESS,
  {
    name: 'fiscal_information',
    data: {
      label: 'Indicação Fiscal',
    },
    html: {
      placeholder: 'Informe a indicação fiscal',
      maxLength: 255,
    },
  },
  {
    name: 'registration_number',
    size: 1 / 2,
    data: {
      label: 'Matrícula',
    },
    html: {
      placeholder: 'Informe a matrícula do imóvel',
      maxLength: 255,
    },
  },
  {
    name: 'registry_office',
    size: 1 / 2,
    data: {
      label: 'Cartório de Registro',
    },
    html: {
      placeholder: 'Informe o cartório de registro',
      maxLength: 255,
    },
  },
  {
    name: 'uploads',
    // size: 1 / 3,
    component: Upload,
    data: {
      label: <Text
        tag="p"
        className="documents-label"
      >
        Por favor adicione algum documento sobre o imóvel informado. Pode ser a transcrição, matrícula, escritura, débito de IPTU, extrato, contrato, entre outros. Caso não tenha documentação para ser anexada, não se preocupe que analisaremos assim mesmo o imóvel cadastrado.
      </Text>,
    },
  },
  {
    name: 'owes_since',
    data: {
      label: 'Deve IPTU desde ano',
    },
    html: {
      placeholder: 'Digite o ano',
      type: 'number',
      maxLength: 4,
      min: 1960,
      max: new Date().getFullYear(),
    },
    validation: currentYearValidation,
  },
  {
    name: 'adult',
    component: CheckBox,
    data: {
      label: 'Você declara que é maior de 18 anos?',
    },
    html: { type: 'checkbox', value: true},
    validation: termsRequiredValidation,
  },
  {
    name: 'accept_terms_conditions',
    component: CheckBox,
    data: {
      label:  <Text
        tag="p"
        style={{ textAlign: 'left', margin: 'auto' /*color: '#01579B'*/ }}
        // size="hint"
      >
              Eu aceito os &nbsp;
        <a
          href="https://iptu.app/terms"
          // eslint-disable-next-line react/jsx-no-target-blank
          target="_blank"
        >Termos de Utilizaçao</a>
      </Text>,
    },
    html: { type: 'checkbox', value: true },
    validation: termsRequiredValidation,
  },
  {
    name: 'accept_privacy_policy',
    component: CheckBox,
    data: {
      label:  <Text
        tag="p"
        style={{ textAlign: 'left', margin: 'auto' /*color: '#01579B'*/ }}
        // size="hint"
      >
              Eu aceito &nbsp;
        <a
          href="https://iptu.app/privacy"
          // eslint-disable-next-line react/jsx-no-target-blank
          target="_blank"
        >Política de Privacidade</a>
      </Text>,
    },
    html: { type: 'checkbox', value: true },
    validation: termsRequiredValidation,
  },
]

export interface LotAreaFarmFormData {
  type: string
  numbers: string
  amount: string
  blocks: string
}
const LOT_AREA_FARM: FieldType<keyof LotAreaFarmFormData>[] = [
  {
    name: 'numbers',
    size: 1 / 3,
    data: {
      label: 'Nº',
    },
    html: {
      placeholder: 'Informe os números',
      maxLength: 255,
    },
    only: values => ['Lote', 'Área', 'Chácara'].includes(values.type),
  },
  {
    name: 'amount',
    size: 1 / 3,
    data: {
      label: 'Quantidade Nº',
    },
    html: {
      placeholder: 'Informe a quantidade de imóveis',
      maxLength: 255,
    },
    only: values => ['Lote', 'Área', 'Chácara'].includes(values.type),
  },
  {
    name: 'blocks',
    size: 1 / 3,
    data: {
      label: 'Quadras Nº',
    },
    html: {
      placeholder: 'Informe as quadras',
      maxLength: 13,
      type:'number',
    },
    only: values => ['Lote', 'Área', 'Chácara'].includes(values.type),
  },
]

export interface OthersFormData {
  type_id: string
  type: string
  custom_type: string
  blocks: string
}
const OTHERS: FieldType<keyof OthersFormData>[] = [
  {
    name: 'custom_type',
    size: 1 / 2,
    data: {
      label: 'Tipo de Imóvel',
    },
    html: {
      placeholder: 'Informe o tipo do imóvel',
      maxLength: 255,
    },
    only: values => values.type === 'Outros',
  },
  {
    name: 'blocks',
    size: 1 / 2,
    data: {
      label: 'Quadras Nº',
    },
    html: {
      placeholder: 'Informe as quadras',
      maxLength: 13,
      type:'number',
    },
    only: values => values.type === 'Outros',
  },
]

export interface RestFormData {
  type_id: string
  type: string
  blocks: string
}
const REST: FieldType<keyof RestFormData>[] = [
  {
    name: 'blocks',
    data: {
      label: 'Quadras Nº',
    },
    html: {
      placeholder: 'Informe as quadras',
      maxLength: 13,
      type:'number',
    },
    only: values => !['Lote', 'Área', 'Chácara', 'Outros'].includes(values.type),
  },
]

export interface RealtyFormData extends
  AddressFormData, CommonFormData,
  LotAreaFarmFormData, OthersFormData,
  RestFormData {
  wants_to_sell: true
}

const fields: FieldType<keyof RealtyFormData>[] = [
  {
    name: 'wants_to_sell',
    component: Hidden,
    data: {},
    html: {},
  },
  {
    name: 'type',
    component: Hidden,
    data: {},
    html: {},
  },

  ...LOT_AREA_FARM,
  ...OTHERS,
  ...REST,

  ...COMMON,
]

export default fields
